<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import { $t } from '@/composables/translations/useTranslate';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

/**
 * Description text clamped to a few lines with a fade-out, expandable via a
 * chevron toggle. The toggle (and the fade) only appear when the text actually
 * overflows the collapsed height.
 *
 * Clamping uses a measured max-height rather than `-webkit-line-clamp`, because
 * HTML descriptions (NBC) nest block elements that line-clamp does not handle.
 */
interface Props {
  html?: string | null;
  text?: string | null;
  lines?: number;
}

const props = withDefaults(defineProps<Props>(), {
  html: null,
  text: null,
  lines: 2,
});

const contentId = useId();
const contentRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const isOverflowing = ref(false);
const collapsedHeight = ref<number | null>(null);
const expandedHeight = ref<number | null>(null);

const sanitizedHtml = computed(() => (props.html ? sanitizeHtml(props.html) : ''));

const hasContent = computed(() =>
  sanitizedHtml.value
    ? sanitizedHtml.value.replace(/<[^>]*>|&nbsp;/g, '').trim() !== ''
    : Boolean(props.text?.trim())
);

/**
 * Both states use a pixel max-height (never `none`) so the switch between them
 * can transition; the expanded value is the measured full content height.
 */
const contentStyle = computed(() => {
  const height = isExpanded.value ? expandedHeight.value : collapsedHeight.value;
  return height !== null ? { maxHeight: `${height}px` } : undefined;
});

const toggleLabel = computed(() =>
  isExpanded.value
    ? $t('ibe.checkout.order-summary.description-collapse', 'Show less')
    : $t('ibe.checkout.order-summary.description-expand', 'Show more')
);

/** Line height in px; `normal` falls back to 1.5 × font size. */
function getLineHeight(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight);
  return Number.isNaN(lineHeight) ? parseFloat(style.fontSize) * 1.5 : lineHeight;
}

/** Recomputes the collapsed and full heights and whether the content overflows. */
function measure(): void {
  const el = contentRef.value;
  if (!el) return;
  const height = Math.ceil(getLineHeight(el) * props.lines);
  collapsedHeight.value = height;
  expandedHeight.value = el.scrollHeight;
  isOverflowing.value = el.scrollHeight > height + 1;
}

function toggle(): void {
  isExpanded.value = !isExpanded.value;
}

const resizeObserver =
  typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;

watch(contentRef, (el, previous) => {
  if (previous) resizeObserver?.unobserve(previous);
  if (el) {
    measure();
    resizeObserver?.observe(el);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(
  () => [props.html, props.text, props.lines],
  async () => {
    isExpanded.value = false;
    await nextTick();
    measure();
  }
);
</script>

<template>
  <div v-if="hasContent" class="flex items-start gap-2 pl-10 mt-2.5">
    <div
      :id="contentId"
      ref="contentRef"
      class="expandable-description__content flex-1 min-w-0 overflow-hidden text-body-small"
      :class="{ 'expandable-description__content--faded': isOverflowing && !isExpanded }"
      :style="contentStyle"
    >
      <div v-if="sanitizedHtml" v-html="sanitizedHtml"></div>
      <p v-else>{{ props.text }}</p>
    </div>

    <button
      v-if="isOverflowing"
      type="button"
      class="shrink-0"
      :aria-expanded="isExpanded"
      :aria-controls="contentId"
      :aria-label="toggleLabel"
      :title="toggleLabel"
      @click="toggle"
    >
      <i
        class="block w-4 h-4 bg-black icon icon-chevron-down-thin transition-transform duration-300"
        :class="{ 'rotate-180': isExpanded }"
        aria-hidden="true"
      ></i>
    </button>
  </div>
</template>

<style scoped>
.expandable-description__content {
  transition: max-height 300ms ease-in-out;
}

@media (prefers-reduced-motion: reduce) {
  .expandable-description__content {
    transition: none;
  }
}

.expandable-description__content--faded {
  -webkit-mask-image: linear-gradient(to bottom, #000 40%, transparent);
  mask-image: linear-gradient(to bottom, #000 40%, transparent);
}

.expandable-description__content :deep(:is(p, li, h1, h2, h3, h4, h5, h6)) {
  font-size: inherit;
  line-height: inherit;
}

.expandable-description__content :deep(p:empty) {
  display: none;
}

.expandable-description__content :deep(p + p),
.expandable-description__content :deep(ul),
.expandable-description__content :deep(ol) {
  margin-top: 0.5em;
}

.expandable-description__content :deep(ul) {
  list-style: disc;
  padding-left: 1.25em;
}

.expandable-description__content :deep(ol) {
  list-style: decimal;
  padding-left: 1.25em;
}

.expandable-description__content :deep(strong) {
  font-weight: 600;
}
</style>
